import { Injectable } from '@angular/core';
import _ from 'lodash';
import { ModeModelBase } from '../model';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';

const buildCreator = (space) => {
  return space
    .creator()
    .then((creator) => {
      space.savedCreator = creator;
      return space;
    })
    .catch((e) => {
      return space;
    });
};

const buildDetailAndCreator = (space) => {
  return space
    .detail()
    .then((detail) => {
      space.savedDetail = detail;
      return buildCreator(space);
    })
    .catch((e) => {
      return buildCreator(space);
    });
};

export class SpaceModel extends ModeModelBase {
  override _name = 'space';

  webLink: any;
  private: boolean;
  community: boolean;
  editable: boolean;
  restricted: any;
  openSpace: boolean;
  iconClassName: string;
  iconName: string;
  defaultAccessIconName: string;
  membershipLink: any;
  pinnable: boolean;
  space_type: any;
  default_access_level: any;

  override idAttribute = 'token';
  override belongsTo = 'account';
  override parentIdAttribute = 'username';
  override hasMany = [
    { name: 'reports', idAttribute: 'token' },
    { name: 'spaceMemberships' },
    { name: 'spaceMemberships', as: 'previewSpaceMemberships' },
    { name: 'spaceReportPins', idAttribute: 'token' },
  ];
  override hasOne = [
    { name: 'account', as: 'creator', idAttribute: 'username' },
    { name: 'spaceMembership', as: 'userSpaceMembership' },
    { name: 'spaceDetail', as: 'detail' },
  ];
  override attributes = [
    { name: 'token', default: null },
    { name: 'name', default: '' },
    { name: 'description', default: '' },
    { name: 'member_count', default: 0 },
    { name: 'report_count', default: 0 },
    { name: 'space_type', default: 'custom' },
    { name: 'restricted', default: false },
    { name: 'free_default', default: false },
    { name: 'default_access_level', default: 'None' },
  ];
  override initialize(resource) {
    if (resource && resource.hasLink('web')) {
      this.webLink = resource.links('web').href;
    }
    if (resource && resource.space_type === 'private') {
      this.private = true;
    }
    if (resource && resource.space_type === 'community') {
      this.community = true;
    }
    if (resource && resource.hasForm('destroy')) {
      this.editable = true;
    }
    if (!this.restricted && !this.community && !this.private) {
      this.openSpace = true;
    }
    this.iconClassName = this.getIconClassName();

    this.iconName = this.getIconName();
    this.defaultAccessIconName = this.getDefaultAccessIconName();

    this.membershipLink =
      resource && resource.hasLink('user_space_membership') ? resource.links('user_space_membership').href : null;

    if (resource) {
      this.webLink = resource.hasLink('web') ? resource.links('web').href : '#';
    }

    if (resource && resource.hasEmbedded('new_space_report_pin')) {
      this.pinnable = true;
    }
  }
  removeSpaceMembershipLink() {
    this.membershipLink = null;
    if (this._resource) {
      this._resource._links.user_space_membership = null;
      this._resource._embedded.user_space_membership = null;
    }
  }
  getIconClassName() {
    if (this.restricted) {
      return 'icon-nav-locked-space';
    }
    switch (this.space_type) {
      case 'private':
        return 'icon-nav-personal-space';
      case 'community':
        return 'icon-nav-community';
      default:
        return 'icon-nav-space-default';
    }
  }
  getIconName() {
    if (this.restricted) {
      return 'padlock-icon';
    }
    switch (this.space_type) {
      case 'private':
        return 'home-icon';
      case 'community':
        return 'globe-icon';
      default:
        return 'collection-icon';
    }
  }
  getDefaultAccessIconName() {
    if (!this.default_access_level) {
      return 'padlock-icon';
    }
    switch (this.space_type) {
      case 'private':
        return 'home-icon';
      case 'community':
        return 'globe-icon';
      default:
        return 'collection-icon';
    }
  }
}

@Injectable({ providedIn: 'root' })
export class SpaceModelAccessor extends ModelAccessorBase<SpaceModel> {
  override name = 'space';
  override idAttribute = 'token';
  override parentModel = 'account';
  override parentIdAttribute = 'username';
  override createAttributes = ['account'];

  constructor(injectables: ModelAccessorInjectables) {
    super(injectables, 'migrated', 'mode.space');
  }

  navbarSort(a, b) {
    if (a.space_type === 'private') return -1;
    if (b.space_type === 'private') return 1;
    if (a.space_type === 'community') return -1;
    if (b.space_type === 'community') return 1;
    if (a.name === 'General') return -1;
    if (b.name === 'General') return 1;
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    return 0;
  }

  override getIdFromLink = (link) => {
    return link.split('?')[0].split('/').pop();
  };
  buildSpacesWithCreator(spaces) {
    return this.injectables.$q.all(_.map(spaces, buildCreator));
  }
  buildSpacesWithCreatorAndDetail(spaces) {
    return this.injectables.$q.all(_.map(spaces, buildDetailAndCreator));
  }
  createModel(resource, opts?) {
    const model = new SpaceModel(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }
}
